// @packages
import React, {
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { IUserState } from '../../utils/types';
import { AuthContextState } from './authState';

// @constants

interface Props {
  children: React.ReactNode;
}

export async function getUserState(): Promise<IUserState | undefined> {
  const userJson = await AsyncStorage.getItem('user');
  return userJson ? JSON.parse(userJson) : undefined;
}

export const AuthProvider = ({ children }: Props) => {
  const [user, setUser] = useState<IUserState|undefined>();
  const [userForgotPassword, setUserForgotPassword] = useState<string>();

  const onGetPreloadData = async () => {
    setUser(await getUserState());
  };

  useEffect(() => {
    onGetPreloadData();
  }, []);

  const handleLogin = async (userInfo: IUserState) => {
    setUser(userInfo);
    await AsyncStorage.setItem('user', JSON.stringify(userInfo));
  };

  const handleLogout = async () => {
    setUser(undefined);
    await AsyncStorage.removeItem('user');
  };

  const handleUserForgotPassword = (userInfo: string) => {
    setUserForgotPassword(userInfo);
  };

  const cleanUserForgotPassword = () => {
    setUserForgotPassword('');
  };

  const value = useMemo(
    () => ({
      user,
      userForgotPassword,
      onLogin: handleLogin,
      onLogout: handleLogout,
      onUserForgotPassword: handleUserForgotPassword,
      onCleanUserForgotPassword: cleanUserForgotPassword
    }),
    [user, userForgotPassword]
  );

  return (
    <AuthContextState.Provider value={value}>
      {children}
    </AuthContextState.Provider>
  );
};

// useAuthContext hook
export const useAuth = () => useContext(AuthContextState);
